import { formattedMoney, formatSolicitorTableLabel } from '../../../helpers/functions';
import { FormGroup, HorizontalRule, Table } from 'arag-ui-lib';
import {
  AVERAGE_EXPECTED_PREMIUM,
  CURRENCY,
  CURRENCY_CODE,
  TARGET_COMMISSION,
  TARGET_UNDERWRITER,
} from '../../../helpers/variables';
import '../../../style/stagedStep2.css';
import StagesDescription from './stagesDescription';
import CONTENT_STRINGS from '../../../helpers/contentStrings';
import { FORM_GROUP_VARIANTS } from '../../../helpers/formGroupVariants';
import { PALE, UNDERWRITER_RATE_PERCENTAGE } from '../../../helpers/constants';
import { useDecimalErrors } from '../../../hooks/useAmountField';

const StagedStep2 = (props) => {
    const {
        stagedPremiumsWizard,
        changeStageSubSetting,
        stagedSubSettings,
        agentsList,
        changeStagedPremiumFormData,
        amountColumnsDisplayDataPerStage,
    } = props;

    const { errors: fieldErrors, checkDecimal } = useDecimalErrors();

    const underwriterRate = {
        id: TARGET_UNDERWRITER,
        name: CONTENT_STRINGS.UNDERWRITER_RATE,
        commissionPercentage: stagedPremiumsWizard.underwriterRatePercentage,
    };
    const commissionData = [underwriterRate, ...agentsList];
    const averageExpectedPremiumData = [
        {
            id: AVERAGE_EXPECTED_PREMIUM, 
            name: CONTENT_STRINGS.VALUE, 
            value: stagedPremiumsWizard.averageExpectedPremium
        }
    ];
    const averageExpectedPremiumColumns = [
        {
            propertyName: AVERAGE_EXPECTED_PREMIUM,
            header: CONTENT_STRINGS.AVERAGE_EXPECTED_PREMIUM,
            id: AVERAGE_EXPECTED_PREMIUM,
            formatter: (v) => {
                return (
                    <div className="fixedWidth">
                        <FormGroup
                            labelSize={"col-sm-7"}
                            inputSize={"col-sm-5"}
                            label={v.name}
                            variant={FORM_GROUP_VARIANTS.CURRENCY}
                            errorMessage={fieldErrors[AVERAGE_EXPECTED_PREMIUM]}
                            inputProps={{
                                id: v.id,
                                name: v.name,
                                value: v.value || '',
                                required: true,
                                handleOptionSelect: () => {},
                                onChange: (e) => { if (!checkDecimal(AVERAGE_EXPECTED_PREMIUM, e.target.value)) changeStagedPremiumFormData(AVERAGE_EXPECTED_PREMIUM, e.target.value); },
                                disabledDropdown: true,
                                menuOptions: [{ sign: `${CURRENCY}`, code: `${CURRENCY_CODE}` },],
                                selectedCurrency: {sign: `${CURRENCY}`, code: `${CURRENCY_CODE}`},
                            }}
                            formGroupTestId={'staged-premium-setting-value'}
                            formGroupLabelTestId={'edit-staged-application-premium-setting-premiums-and-loi-underwriter-rate'}
                            formGroupInputTestId={'edit-staged-application-premium-setting-premiums-and-loi-value'}
                        />
                    </div>
                );
            },
        },
    ];
    const ratesColumns = [
        {
            propertyName: 'rates',
            header: CONTENT_STRINGS.RATES,
            sortable: false,
            id: 'rates',
            type: 'number',
            formatter: (v) => {
                const agentIndex = agentsList.findIndex(a => a.name === v.name);
                const formGroupTestId = v.id === UNDERWRITER_RATE_PERCENTAGE
                ?
                    'staged-premium-setting-rate-underwriter'
                :
                    `staged-premium-setting-rate-agent-${agentIndex}`;
                // Only agent rows (not the Underwriter Rate row) carry an ARAG provider. Wrapped in
                // .agent-rate-label so the marker renders inline with the required asterisk - see
                // stagedStep2.css.
                const rateLabel = v.id !== TARGET_UNDERWRITER
                    ? <span className={"agent-rate-label"}>{formatSolicitorTableLabel({ label: v.name, aragProvider: v.aragProvider })}</span>
                    : v.name;
                return (
                    <div className="fixedWidth">
                        <FormGroup
                            labelSize={"col-sm-7"}
                            inputSize={"col-sm-5"}
                            label={rateLabel}
                            variant={FORM_GROUP_VARIANTS.CURRENCY}
                            errorMessage={fieldErrors[`rate-${v.id}`]}
                            inputProps={{
                                id: `${v.id}`,
                                name: v.name,
                                required: true,
                                value: v.commissionPercentage,
                                handleOptionSelect: () => {},
                                onChange: (e) => { if (!checkDecimal(`rate-${v.id}`, e.target.value)) v.id == TARGET_UNDERWRITER ? changeStagedPremiumFormData(TARGET_UNDERWRITER, e.target.value) : changeStageSubSetting(v.id, TARGET_COMMISSION, e.target.value); },
                                disabledDropdown: true,
                                menuOptions: [{ sign: '%', code: '%' }],
                                selectedCurrency: { sign: '%', code: '%' },
                            }}
                            formGroupTestId={formGroupTestId}
                        />
                    </div>
                );
            },
        },
    ];
    const stagesColumns = [
        {
            propertyName: 'stageName',
            header: CONTENT_STRINGS.STAGE_OF_POLICY,
            id: 'stageName',
            formatter: (v) => {
                return(
                    <FormGroup
                        labelSize={'col-sm-6'}
                        inputSize={'col-sm-6'}
                        label={v.stageName}
                        variant={FORM_GROUP_VARIANTS.PLAIN}
                        inputProps={{
                            id: v.stageName,
                            name: v.stageName,
                        }}
                        formGroupTestId={`staged-premium-setting-heading-stage-${v.stageMachineName}`}
                    />
                );
            },
        },
        {
            propertyName: 'fields',
            header: CONTENT_STRINGS.AMOUNTS,
            id: 'fields',
            formatter: (v) => {
                return amountColumnsDisplayDataPerStage(v).map((c, index) => {
                    const stage = v.stageMachineName;
                    let formGroupTestId;
                    if(c.title === CONTENT_STRINGS.PREMIUM){
                        formGroupTestId = `staged-premium-setting-stage-${stage}-premium`;
                    } else if(c.title === CONTENT_STRINGS.LIMIT_OF_INDEMNITY){
                        formGroupTestId = `staged-premium-setting-stage-${stage}-limit-of-indemnity`;
                    } else if(c.title === CONTENT_STRINGS.UNDERWRITER_RATE){
                        formGroupTestId = `staged-premium-setting-stage-${stage}-underwriter-rate`;
                    } else {
                        let agentIndex = agentsList.findIndex(a => a.name === c.title);
                        formGroupTestId = `staged-premium-setting-stage-${stage}-agent-rate-${agentIndex}`;
                    }
                    // Always display money at 2dp regardless of the real underlying precision.
                    const formattedValue = formattedMoney(c.value, CURRENCY);
                    const rowLabel = c.isCommissionAgent
                        ? formatSolicitorTableLabel({ label: c.title, aragProvider: c.aragProvider })
                        : c.title;
                    return c.isPlain
                    ?
                        <div key={`${c.title}-${index}`}>
                            <FormGroup
                                labelSize={'col-sm-6'}
                                inputSize={'col-sm-6'}
                                label={rowLabel}
                                variant={FORM_GROUP_VARIANTS.PLAIN}
                                inputProps={{
                                    id: c.title,
                                    name: c.title,
                                    label: formattedValue,
                                }}
                                formGroupTestId={formGroupTestId}
                            />
                            <HorizontalRule variant={PALE} />
                        </div>
                    :
                        <div key={`${c.title}-${index}`} className="fixedWidth">
                            <FormGroup
                                labelSize={"col-sm-6"}
                                inputSize={"col-sm-6"}
                                label={rowLabel}
                                variant={FORM_GROUP_VARIANTS.CURRENCY}
                                errorMessage={fieldErrors[formGroupTestId]}
                                inputProps={{
                                    id: c.id,
                                    name: c.title,
                                    required: true,
                                    value: c.value,
                                    onChange: (e) => { if (!checkDecimal(formGroupTestId, e.target.value)) c.onChange(e); },
                                    disabledDropdown: true,
                                    handleOptionSelect: () => {},
                                    menuOptions: [{ sign: `${CURRENCY}`, code: `${CURRENCY_CODE}` },],
                                    selectedCurrency: { sign: `${CURRENCY}`, code: `${CURRENCY_CODE}`, },
                                }}
                                formGroupTestId={formGroupTestId}
                            />
                            <HorizontalRule variant={PALE} />
                        </div>
                });
            },
        },
    ];
    return (
        <div>
            <Table
                orderAsc={false}
                striped
                columns={ratesColumns}
                data={commissionData}
                tableWrapperTestId={'staged-premium-setting-rates'}
            />
            <Table
                orderAsc={false}
                striped
                columns={averageExpectedPremiumColumns}
                data={averageExpectedPremiumData}
                tableWrapperTestId={'staged-premium-setting-average-expected-premium'}
            />
            <Table
                orderAsc={false}
                striped
                columns={stagesColumns}
                data={stagedSubSettings != undefined ? stagedSubSettings : []}
                tableWrapperTestId={'staged-premium-setting-stages'}
            />
            <StagesDescription
                stageATestId={'staged-premium-setting-heading-stage-a'}
                stageBTestId={'staged-premium-setting-heading-stage-b'}
                stageCTestId={'staged-premium-setting-heading-stage-c'}
            />
        </div>
    );
};
export default StagedStep2;
